/** Kiosk lifecycle events — catalog 1.10.0 additions (capability dictionary v9 is separate). */
import {
  asEventRecord,
  defineEvent,
  optionalString,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

export interface KioskTemplatePublishedPayload extends Record<string, unknown> {
  templateId: string;
  orgId: string;
  version: number;
  publishedBy: string;
  publishedAt: string;
}

export interface KioskDeviceLifecyclePayload extends Record<string, unknown> {
  deviceId: string;
  orgId: string;
  storeId?: string;
  workspaceId?: string;
}

function devicePayload(type: string): EventSchema<KioskDeviceLifecyclePayload> {
  return {
    parse(data) {
      const record = asEventRecord(data, type);
      const storeId = optionalString(record.storeId, 'storeId', type);
      const workspaceId = optionalString(record.workspaceId, 'workspaceId', type);
      return {
        deviceId: requireString(record.deviceId, 'deviceId', type),
        orgId: requireString(record.orgId, 'orgId', type),
        ...(storeId === undefined ? {} : { storeId }),
        ...(workspaceId === undefined ? {} : { workspaceId }),
      };
    },
  };
}

const KIOSK_TEMPLATE_PUBLISHED_TYPE = 'kiosks:template-published';
const kioskTemplatePublishedSchema: EventSchema<KioskTemplatePublishedPayload> = {
  parse(data) {
    const record = asEventRecord(data, KIOSK_TEMPLATE_PUBLISHED_TYPE);
    const version = record.version;
    if (typeof version !== 'number' || !Number.isSafeInteger(version) || version < 1) {
      throw new Error(`${KIOSK_TEMPLATE_PUBLISHED_TYPE}: field "version" must be a positive safe integer`);
    }
    const publishedAt = requireString(record.publishedAt, 'publishedAt', KIOSK_TEMPLATE_PUBLISHED_TYPE);
    if (Number.isNaN(Date.parse(publishedAt))) {
      throw new Error(`${KIOSK_TEMPLATE_PUBLISHED_TYPE}: field "publishedAt" must be a valid date string`);
    }
    return {
      templateId: requireString(record.templateId, 'templateId', KIOSK_TEMPLATE_PUBLISHED_TYPE),
      orgId: requireString(record.orgId, 'orgId', KIOSK_TEMPLATE_PUBLISHED_TYPE),
      version,
      publishedBy: requireString(record.publishedBy, 'publishedBy', KIOSK_TEMPLATE_PUBLISHED_TYPE),
      publishedAt,
    };
  },
};

export const KIOSK_TEMPLATE_PUBLISHED_EVENT = defineEvent<KioskTemplatePublishedPayload>({
  type: KIOSK_TEMPLATE_PUBLISHED_TYPE,
  version: 1,
  schema: kioskTemplatePublishedSchema,
  auditable: true,
  description: 'A kiosk template version was published and frozen.',
  since: '1.10.0',
});

export const KIOSK_DEVICE_REGISTERED_EVENT = defineEvent<KioskDeviceLifecyclePayload>({
  type: 'kiosks:device-registered', version: 1, schema: devicePayload('kiosks:device-registered'),
  auditable: true, description: 'A kiosk device was registered.', since: '1.10.0',
});

export const KIOSK_DEVICE_DISABLED_EVENT = defineEvent<KioskDeviceLifecyclePayload>({
  type: 'kiosks:device-disabled', version: 1, schema: devicePayload('kiosks:device-disabled'),
  auditable: true, description: 'A kiosk device was disabled and its scoped policies revoked.', since: '1.10.0',
});

export const KIOSK_DEVICE_KEY_ROTATED_EVENT = defineEvent<KioskDeviceLifecyclePayload>({
  type: 'kiosks:device-key-rotated', version: 1, schema: devicePayload('kiosks:device-key-rotated'),
  auditable: true, description: 'A kiosk device received a replacement key and policy scope.', since: '1.10.0',
});

export const KIOSKS_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [KIOSK_TEMPLATE_PUBLISHED_EVENT.type]: KIOSK_TEMPLATE_PUBLISHED_EVENT,
  [KIOSK_DEVICE_REGISTERED_EVENT.type]: KIOSK_DEVICE_REGISTERED_EVENT,
  [KIOSK_DEVICE_DISABLED_EVENT.type]: KIOSK_DEVICE_DISABLED_EVENT,
  [KIOSK_DEVICE_KEY_ROTATED_EVENT.type]: KIOSK_DEVICE_KEY_ROTATED_EVENT,
};
